// src/components/EngeRaeume.js
import React, { useState, useEffect, useRef } from "react";
import Papa from "papaparse";
import * as XLSX from 'xlsx-js-style'; // SheetJS Import für Excel-Unterstützung
import {
  autoReconnectDirectory,
  hasSelectedDirectory,
  selectDirectory,
  saveFileToDirectory,
  listFilesInDirectory
} from "../utils/fileSystem";
import "./EngeRaeume.css";
import TankbestellungModal from "./TankbestellungModal";

function EngeRaeume() {
  // State für Formularfelder
  const [abgabeDatum, setAbgabeDatum] = useState("");
  const [freigabeVon, setFreigabeVon] = useState("");
  const [freigabeBis, setFreigabeBis] = useState("");
  const [tanks, setTanks] = useState("");
  const [firma, setFirma] = useState("");
  const [entries, setEntries] = useState([]);
  const [laufendeNummer, setLaufendeNummer] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState({ text: "", type: "" }); // Für Feedback-Nachrichten
  const [searchTerm, setSearchTerm] = useState(""); // Für die Suche
  const [searchDate, setSearchDate] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedTank, setSelectedTank] = useState('all');
  const [selectedFirma, setSelectedFirma] = useState('all');
  const [selectedID, setSelectedID] = useState('all');
  const [selectedFreigemessen, setSelectedFreigemessen] = useState('all');
  
  // State für das TankbestellungModal
  const [showTankModal, setShowTankModal] = useState(false);
  const [tanksForTomorrow, setTanksForTomorrow] = useState([]);
  const [tomorrowFormattedDate, setTomorrowFormattedDate] = useState("");
  const [isDirectorySelected, setIsDirectorySelected] = useState(false);
  const [sortByIdDesc, setSortByIdDesc] = useState(true);
  const [showEmailSettings, setShowEmailSettings] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailCC, setEmailCC] = useState('');
  const [activeTimePicker, setActiveTimePicker] = useState(null); // {entryId, field: 'arbeitsbeginn'|'arbeitsende'}
  const [trashEntries, setTrashEntries] = useState([]);
  const [showTrash, setShowTrash] = useState(false);

  // State für das Bearbeiten-Modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);

  // Konstanten für Datei-Operationen
  const CATEGORY = "engeraume";
  const FILE_PREFIX = "Liste enge Räume FDB";
  const FILE_EXTENSION = ".xlsx";
  const MAIN_FILE_NAME = `${FILE_PREFIX}${FILE_EXTENSION}`;
  const TRASH_FILE_PREFIX = "Papierkorb enge Räume FDB";
  const TRASH_FILE_NAME = `${TRASH_FILE_PREFIX}${FILE_EXTENSION}`;

  const fileInputRef = React.useRef();

  const [currentUser, setCurrentUser] = useState(null);

  // Beim Start der Komponente:
  // 1. Automatisch mit dem letzten Verzeichnis verbinden
  // 2. Daten aus der neuesten Datei laden
  useEffect(() => {
    const initializeComponent = async () => {
      setIsLoading(true);
      setMessage({ text: "Initialisiere Komponente...", type: "info" });

      try {
        const hasDirectory = await hasSelectedDirectory(CATEGORY);
        setIsDirectorySelected(hasDirectory);

        if (hasDirectory) {
          const reconnected = await autoReconnectDirectory(CATEGORY);

          if (reconnected) {
            await Promise.all([
              loadNewestDataFile(),
              loadTrashData() // Lade auch die Papierkorb-Daten
            ]);
          } else {
            setMessage({ 
              text: "Verbindung zum Speicherort nicht möglich. Bitte wählen Sie erneut einen Speicherort in den Einstellungen aus.", 
              type: "warning" 
            });
          }
        } else {
          setMessage({ 
            text: "Kein Speicherort konfiguriert. Bitte wählen Sie einen Speicherort in den Einstellungen aus.", 
            type: "info" 
          });
        }
      } catch (error) {
        console.error("Fehler bei der Initialisierung:", error);
        setMessage({ 
          text: `Fehler beim Initialisieren: ${error.message}`, 
          type: "error" 
        });
      } finally {
        setIsLoading(false);
      }
    };

    initializeComponent();
  }, []);

  useEffect(() => {
    // Lade aktuellen User aus localStorage
    const userStr = localStorage.getItem("currentUser");
    if (userStr) {
      setCurrentUser(JSON.parse(userStr));
    }
  }, []);

  // Funktion zum Laden der Hauptdaten-Datei (immer gleiche Datei)
  const loadNewestDataFile = async () => {
    try {
      setIsLoading(true);
      setMessage({ text: "Lade Daten aus Speicherort...", type: "info" });

      // Immer nur die eine Hauptdatei laden
      const files = await listFilesInDirectory(CATEGORY, FILE_PREFIX, FILE_EXTENSION);
      const mainFile = files.find(file => file.name === MAIN_FILE_NAME);

      if (!mainFile) {
        setMessage({ 
          text: "Keine Datendatei im Speicherort gefunden. Sie können neue Einträge erstellen.", 
          type: "info" 
        });
        setIsLoading(false);
        return;
      }

      // Datei als ArrayBuffer lesen und mit SheetJS verarbeiten
      const arrayBuffer = await mainFile.file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // Erstes Arbeitsblatt auswählen
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      
      // Daten in JSON umwandeln
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      
      if (jsonData && jsonData.length > 0) {
        // Daten in das interne Format konvertieren
        const formattedEntries = jsonData.map((row, index) => ({
          id: `excel-${index}-${row.Datum || row.datum || index}`,
          nummer: row.ID || row.Nummer || row.nummer || index + 1,
          datum: row.Datum || row.datum || "",
          abgabeDatum: row["Abgabe Schein"] || row.abgabeDatum || "",
          tanks: row.Tanks || row.tanks || "",
          firma: row.Firma || row.firma || "",
          arbeitsbeginn: row.Arbeitsbeginn || row.arbeitsbeginn || "",
          arbeitsende: row.Arbeitsende || row.arbeitsende || "",
          freigemessen: (row.Freigemessen === "Ja" || row.freigemessen === true || row.freigemessen === "true") ? true : false,
          bemerkung: row.Bemerkungen || row.Bemerkung || row.bemerkung || "",
        }));
        
        setEntries(formattedEntries);
        
        // Bestimme die nächste laufende Nummer
        const highestNumber = Math.max(...formattedEntries.map(e => Number(e.nummer) || 0), 0);
        setLaufendeNummer(highestNumber + 1);
        
        setMessage({ 
          text: `Daten erfolgreich aus ${mainFile.name} geladen (${formattedEntries.length} Einträge)`, 
          type: "success" 
        });
      } else {
        setMessage({ 
          text: "Die Datei enthält keine gültigen Daten", 
          type: "warning" 
        });
      }
    } catch (error) {
      console.error("Fehler beim Laden der Hauptdatei:", error);
      setMessage({ 
        text: `Fehler beim Laden der Daten: ${error.message}`, 
        type: "error" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Funktion zum Laden der Papierkorb-Daten
  const loadTrashData = async () => {
    try {
      const files = await listFilesInDirectory(CATEGORY, TRASH_FILE_PREFIX, FILE_EXTENSION);
      
      if (files.length === 0) {
        setTrashEntries([]);
        return;
      }

      // Suche nach der Datei mit dem festen Namen
      const trashFile = files.find(file => file.name === TRASH_FILE_NAME);
      if (!trashFile) {
        setTrashEntries([]);
        return;
      }

      const arrayBuffer = await trashFile.file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      
      if (jsonData && jsonData.length > 0) {
        const formattedTrashEntries = jsonData.map((row, index) => ({
          id: `trash-${index}-${row.Datum || row.datum || index}`,
          nummer: row.ID || row.Nummer || row.nummer || index + 1,
          datum: row.Datum || row.datum || "",
          abgabeDatum: row["Abgabe Schein"] || row.abgabeDatum || "",
          tanks: row.Tanks || row.tanks || "",
          firma: row.Firma || row.firma || "",
          arbeitsbeginn: row.Arbeitsbeginn || row.arbeitsbeginn || "",
          arbeitsende: row.Arbeitsende || row.arbeitsende || "",
          freigemessen: (row.Freigemessen === "Ja" || row.freigemessen === true || row.freigemessen === "true") ? true : false,
          bemerkung: row.Bemerkung || row.bemerkung || "",
          deletedAt: row["Gelöscht am"] || row.deletedAt || new Date().toISOString()
        }));
        
        setTrashEntries(formattedTrashEntries);
      }
    } catch (error) {
      console.error("Fehler beim Laden der Papierkorb-Daten:", error);
      setMessage({ 
        text: `Fehler beim Laden der Papierkorb-Daten: ${error.message}`, 
        type: "error" 
      });
    }
  };

  // Funktion zum Speichern der Papierkorb-Daten
  const saveTrashData = async (entriesToSave) => {
    try {
      if (!isDirectorySelected) {
        setMessage({ 
          text: "Bitte wählen Sie zuerst einen Speicherort aus.", 
          type: "warning" 
        });
        return;
      }

      // Daten für Excel vorbereiten
      const data = entriesToSave.map(entry => ({
        ID: entry.nummer,
        Datum: entry.datum,
        "Abgabe Schein": entry.abgabeDatum,
        Tanks: entry.tanks,
        Firma: entry.firma,
        Arbeitsbeginn: entry.arbeitsbeginn,
        Arbeitsende: entry.arbeitsende,
        Freigemessen: entry.freigemessen ? "Ja" : "Nein",
        Bemerkung: entry.bemerkung || "",
        "Gelöscht am": entry.deletedAt
      }));

      // Arbeitsmappe erstellen
      const workbook = XLSX.utils.book_new();
      
      // Arbeitsblatt aus Daten erstellen
      const worksheet = XLSX.utils.json_to_sheet(data);
      
      // Spaltenbreiten anpassen
      const columnWidths = [
        { wch: 5 },    // ID
        { wch: 12 },   // Datum
        { wch: 12 },   // Abgabe Schein
        { wch: 25 },   // Tanks
        { wch: 20 },   // Firma
        { wch: 12 },   // Arbeitsbeginn
        { wch: 12 },   // Arbeitsende
        { wch: 12 },   // Freigemessen
        { wch: 30 },   // Bemerkung
        { wch: 20 }    // Gelöscht am
      ];
      worksheet['!cols'] = columnWidths;

      // Arbeitsblatt zur Arbeitsmappe hinzufügen
      XLSX.utils.book_append_sheet(workbook, worksheet, "Papierkorb");

      // Excel-Datei als Arraybuffer erstellen
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      // Immer die gleiche Datei verwenden
      await saveFileToDirectory(CATEGORY, TRASH_FILE_NAME, blob);
      
      setMessage({ 
        text: "Papierkorb-Daten erfolgreich gespeichert", 
        type: "success" 
      });
    } catch (error) {
      console.error("Fehler beim Speichern der Papierkorb-Daten:", error);
      setMessage({ 
        text: `Fehler beim Speichern der Papierkorb-Daten: ${error.message}`, 
        type: "error" 
      });
    }
  };

  // Neue Löschfunktion für einzelne Zeile (Papierkorb)
  const handleDeleteById = async (id) => {
    const entryToDelete = entries.find(entry => entry.id === id);
    if (entryToDelete) {
      const entryWithTimestamp = {
        ...entryToDelete,
        deletedAt: new Date().toISOString()
      };
      const newTrashEntries = [...trashEntries, entryWithTimestamp];
      setTrashEntries(newTrashEntries);
      setEntries(prev => prev.filter(entry => entry.id !== id));
      await saveTrashData(newTrashEntries);
      setMessage({ text: "Eintrag in den Papierkorb verschoben", type: "success" });
    }
  };

  // Handler für Wiederherstellen
  const handleRestoreFromTrash = async (id) => {
    const entryToRestore = trashEntries.find(entry => entry.id === id);
    if (entryToRestore) {
      const { deletedAt, ...entryWithoutTimestamp } = entryToRestore;
      setEntries(prev => [...prev, entryWithoutTimestamp]);
      const newTrashEntries = trashEntries.filter(entry => entry.id !== id);
      setTrashEntries(newTrashEntries);
      await saveTrashData(newTrashEntries);
      setMessage({ text: "Eintrag wiederhergestellt", type: "success" });
    }
  };

  // Handler für endgültiges Löschen (nur Admin)
  const handleDeleteForever = async (id) => {
    if (!isAdmin) {
      setMessage({ text: "Nur Administratoren können Einträge endgültig löschen", type: "error" });
      return;
    }
    const newTrashEntries = trashEntries.filter(entry => entry.id !== id);
    setTrashEntries(newTrashEntries);
    await saveTrashData(newTrashEntries);
    setMessage({ text: "Eintrag endgültig gelöscht", type: "success" });
  };

  // Datei-Import-Funktion - unterstützt Excel und CSV
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsLoading(true);
    setMessage({ text: "", type: "" });

    // Dateityp prüfen
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    
    if (isExcel) {
      // Excel-Dateien verarbeiten
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Erstes Arbeitsblatt auswählen
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Daten in JSON umwandeln
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          if (jsonData && jsonData.length > 0) {
            // Daten in das interne Format konvertieren
            const parsedEntries = jsonData.map((row, index) => ({
              id: `excel-import-${index}-${row.Datum || row.datum || index}`,
              nummer: row.ID || row.Nummer || row.nummer || laufendeNummer + index,
              datum: row.Datum || row.datum || "",
              abgabeDatum: row["Abgabe Schein"] || row.abgabeDatum || "",
              tanks: row.Tanks || row.tanks || "",
              firma: row.Firma || row.firma || "",
              arbeitsbeginn: row.Arbeitsbeginn || row.arbeitsbeginn || "",
              arbeitsende: row.Arbeitsende || row.arbeitsende || "",
              freigemessen: (row.Freigemessen === "Ja" || row.freigemessen === true || row.freigemessen === "true") ? true : false,
              bemerkung: row.Bemerkungen || row.bemerkung || "",
            }));
            
            setEntries((prev) => [...prev, ...parsedEntries]);
            
            // Erhöhe die laufende Nummer
            const highestNumber = Math.max(
              ...parsedEntries.map(e => Number(e.nummer) || 0),
              laufendeNummer
            );
            setLaufendeNummer(highestNumber + 1);
            
            setMessage({ text: `${parsedEntries.length} Einträge aus Excel importiert`, type: "success" });
          } else {
            setMessage({ text: "Keine Daten in der Excel-Datei gefunden", type: "warning" });
          }
        } catch (error) {
          console.error("Fehler beim Verarbeiten der Excel-Datei:", error);
          setMessage({ text: `Excel-Import fehlgeschlagen: ${error.message}`, type: "error" });
        } finally {
          setIsLoading(false);
        }
      };
      
      reader.onerror = (error) => {
        console.error("Fehler beim Lesen der Excel-Datei:", error);
        setMessage({ text: `Datei konnte nicht gelesen werden: ${error.message}`, type: "error" });
        setIsLoading(false);
      };
      
      reader.readAsArrayBuffer(file);
    } else {
      // CSV-Dateien verarbeiten
      Papa.parse(file, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        complete: (result) => {
          if (result.data && result.data.length > 0) {
            const parsedEntries = result.data.map((row, index) => ({
              id: `csv-import-${index}-${row.datum || index}`,
              nummer: row.nummer || laufendeNummer + index,
              datum: row.datum || "",
              abgabeDatum: row.abgabeDatum || "",
              tanks: row.tanks || "",
              firma: row.firma || "",
              arbeitsbeginn: row.arbeitsbeginn || "",
              arbeitsende: row.arbeitsende || "",
              freigemessen: (row.freigemessen === true || row.freigemessen === "Ja") ? true : false,
              bemerkung: row.Bemerkungen || row.bemerkung || "",
            }));
            
            setEntries((prev) => [...prev, ...parsedEntries]);
            
            // Erhöhe die laufende Nummer
            const highestNumber = Math.max(
              ...parsedEntries.map(e => Number(e.nummer) || 0),
              laufendeNummer
            );
            setLaufendeNummer(highestNumber + 1);
            
            setMessage({ text: `${parsedEntries.length} Einträge importiert`, type: "success" });
          } else {
            setMessage({ text: "Keine Daten in der Datei gefunden", type: "warning" });
          }
          setIsLoading(false);
        },
        error: (error) => {
          console.error("Fehler beim Parsen der CSV-Datei:", error);
          setMessage({ text: `Import fehlgeschlagen: ${error.message}`, type: "error" });
          setIsLoading(false);
        }
      });
    }
  };

  // Funktion zum Hinzufügen von Einträgen
  const handleAddEntry = () => {
    // Validierung
    if (!abgabeDatum || !freigabeVon || !freigabeBis || !tanks || !firma) {
      setMessage({ text: "Bitte füllen Sie alle Pflichtfelder aus", type: "error" });
      return;
    }

    // Datumsbereiche prüfen
    const startDate = new Date(freigabeVon);
    const endDate = new Date(freigabeBis);
    
    if (startDate > endDate) {
      setMessage({ text: "Das Startdatum muss vor dem Enddatum liegen", type: "error" });
      return;
    }

    const dayCount = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
    const newEntries = [];

    for (let i = 0; i < dayCount; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);

      newEntries.push({
        id: `${laufendeNummer}-${date.toISOString().split("T")[0]}`,
        nummer: laufendeNummer,
        datum: date.toISOString().split("T")[0],
        abgabeDatum,
        tanks,
        firma,
        arbeitsbeginn: "",
        arbeitsende: "",
        freigemessen: false,
        bemerkung: "",
      });
    }

    setEntries((prev) => [...prev, ...newEntries]);
    setLaufendeNummer((prev) => prev + 1);
    setMessage({ text: `${newEntries.length} Einträge erstellt`, type: "success" });

    // Formularfelder zurücksetzen
    setAbgabeDatum("");
    setFreigabeVon("");
    setFreigabeBis("");
    setTanks("");
    setFirma("");
  };

  // Arbeitszeitänderung (Beginn/Ende)
  const handleArbeitszeitChange = (id, field, value) => {
    setEntries((prev) =>
      prev.map((entry) =>
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    );
  };

  // Freigemessen-Status ändern
  const handleFreigemessenChange = (id, checked) => {
    setEntries((prev) =>
      prev.map((entry) =>
        entry.id === id ? { ...entry, freigemessen: checked } : entry
      )
    );
  };

  // Daten ausschließlich im lokalen Speicherort als Excel speichern
  const handleSaveData = async () => {
    setIsLoading(true);
    setMessage({ text: "Speichere Daten...", type: "info" });

    try {
      // Prüfen, ob ein Verzeichnis ausgewählt ist
      if (!isDirectorySelected) {
        const dirSelected = await selectDirectory(CATEGORY);
        
        if (!dirSelected) {
          setMessage({ 
            text: "Kein Speicherort ausgewählt. Die Daten wurden nicht gespeichert.", 
            type: "error" 
          });
          setIsLoading(false);
          return;
        }
        setIsDirectorySelected(true);
      }

      // Einträge nach ID absteigend sortieren
      const sortedEntries = [...entries].sort((a, b) => (Number(b.nummer) || 0) - (Number(a.nummer) || 0));

      // Daten für Excel vorbereiten
      const excelData = sortedEntries.map(entry => ({
        ID: entry.nummer,
        Datum: entry.datum,
        "Abgabe Schein": entry.abgabeDatum,
        Tanks: entry.tanks,
        Firma: entry.firma,
        "Arbeitsbeginn": entry.arbeitsbeginn,
        "Arbeitsende": entry.arbeitsende,
        Freigemessen: entry.freigemessen ? "Ja" : "Nein",
        Bemerkungen: entry.bemerkung || ""
      }));

      // Arbeitsmappe erstellen
      const workbook = XLSX.utils.book_new();
      
      // Arbeitsblatt aus Daten erstellen
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      
      // Spaltenbreiten anpassen
      const columnWidths = [
        { wch: 5 },    // ID
        { wch: 12 },   // Datum
        { wch: 12 },   // Abgabe Schein
        { wch: 25 },   // Tanks
        { wch: 20 },   // Firma
        { wch: 12 },   // Arbeitsbeginn
        { wch: 12 },   // Arbeitsende
        { wch: 12 },   // Bemerkungen
        { wch: 12 }    // Freigemessen
      ];
      worksheet['!cols'] = columnWidths;

      // Arbeitsblatt zur Arbeitsmappe hinzufügen
      XLSX.utils.book_append_sheet(workbook, worksheet, "Enge Räume");
      
      // Excel-Datei als Arraybuffer erstellen
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      
      // Immer die gleiche Datei verwenden
      await saveFileToDirectory(CATEGORY, MAIN_FILE_NAME, excelBuffer);
      
      setMessage({ 
        text: `Daten erfolgreich als Excel-Datei gespeichert: ${MAIN_FILE_NAME}`, 
        type: "success" 
      });
    } catch (error) {
      console.error("Fehler beim Speichern:", error);
      let errorMessage = "Fehler beim Speichern";
      if (error.name === 'NotAllowedError') {
        errorMessage = "Keine Berechtigung zum Speichern im ausgewählten Verzeichnis. Bitte wählen Sie erneut einen Speicherort aus.";
      } else if (error.name === 'SecurityError') {
        errorMessage = "Sicherheitsfehler beim Zugriff auf das Dateisystem. Bitte verwenden Sie einen modernen Browser wie Chrome oder Edge.";
      } else {
        errorMessage = `Fehler beim Speichern: ${error.message}`;
      }
      setMessage({ text: errorMessage, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // Excel exportieren als Download für den Benutzer
  const handleExportExcel = () => {
    try {
      // Daten für Excel vorbereiten
      const excelData = entries.map(entry => ({
        ID: entry.nummer,
        Datum: entry.datum,
        "Abgabe Schein": entry.abgabeDatum,
        Tanks: entry.tanks,
        Firma: entry.firma,
        "Arbeitsbeginn": entry.arbeitsbeginn,
        "Arbeitsende": entry.arbeitsende,
        Freigemessen: entry.freigemessen ? "Ja" : "Nein",
        Bemerkungen: entry.bemerkung || ""
      }));

      // Arbeitsmappe erstellen
      const workbook = XLSX.utils.book_new();
      
      // Arbeitsblatt aus Daten erstellen
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      
      // Spaltenbreiten anpassen
      const columnWidths = [
        { wch: 5 },    // ID
        { wch: 12 },   // Datum
        { wch: 12 },   // Abgabe Schein
        { wch: 25 },   // Tanks
        { wch: 20 },   // Firma
        { wch: 12 },   // Arbeitsbeginn
        { wch: 12 },   // Arbeitsende
        { wch: 12 },   // Bemerkungen
        { wch: 12 }    // Freigemessen
      ];
      worksheet['!cols'] = columnWidths;

      // Arbeitsblatt zur Arbeitsmappe hinzufügen
      XLSX.utils.book_append_sheet(workbook, worksheet, "Enge Räume");
      
      // Excel-Datei als Blob erstellen
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Download-Link erstellen
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      
      // Dateinamen mit Datum erstellen
      const heute = new Date();
      const datumStr = heute.toISOString().split('T')[0]; // YYYY-MM-DD
      
      link.href = url;
      link.download = `${FILE_PREFIX} ${datumStr}${FILE_EXTENSION}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setMessage({ text: "Excel-Datei exportiert", type: "success" });
    } catch (error) {
      console.error("Fehler beim Exportieren:", error);
      setMessage({ text: `Fehler beim Exportieren: ${error.message}`, type: "error" });
    }
  };

  // Funktion zum Finden der Tanks für morgen und E-Mail-Preview öffnen
  const findTanksForTomorrow = () => {
    // Hilfsfunktion, um das Datum im Format YYYY-MM-DD zu formatieren
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
  
    try {
      // Datum für morgen berechnen
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = formatDate(tomorrow);
      
      // Formatiertes Datum für die Anzeige (z.B. "16. April 2025")
      const formattedDate = tomorrow.toLocaleDateString('de-DE', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      
      console.log("Gesuchtes Datum für morgen:", tomorrowStr);
      console.log("Alle Einträge:", entries);
  
      // Einträge für morgen filtern
      const entriesForTomorrow = entries.filter(entry => {
        const isTomorrow = entry.datum === tomorrowStr;
        const isNotFreigemessen = !entry.freigemessen;
        console.log(`Eintrag: ${entry.id}, Datum: ${entry.datum}, isTomorrow: ${isTomorrow}, isNotFreigemessen: ${isNotFreigemessen}`);
        return isTomorrow && isNotFreigemessen;
      });
  
      console.log("Gefilterte Einträge für morgen:", entriesForTomorrow);
  
      if (entriesForTomorrow.length > 0) {
        // Einträge und formatiertes Datum für das Modal setzen
        setTanksForTomorrow(entriesForTomorrow);
        setTomorrowFormattedDate(formattedDate);
        setShowTankModal(true);
        
        // Erfolgs-Nachricht anzeigen
        setMessage({ 
          text: `E-Mail-Vorschau für ${entriesForTomorrow.length} Tank(s) geöffnet.`, 
          type: "success"
        });
      } else {
        // Für Testzwecke: Modal trotzdem öffnen mit leeren Einträgen
        setTanksForTomorrow([]);
        setTomorrowFormattedDate(formattedDate);
        setShowTankModal(true);
        
        setMessage({ 
          text: `Keine Tanks für morgen (${tomorrowStr}) gefunden. Modal wird trotzdem geöffnet.`, 
          type: "info"
        });
      }
    } catch (error) {
      console.error("Fehler bei der Tanksuche:", error);
      setMessage({ 
        text: `Fehler bei der Suche nach Tanks für morgen: ${error.message}`, 
        type: "error"
      });
    }
  };

  // Handler zum Öffnen des Modals
  const handleEditEntry = (entry) => {
    setEditEntry(entry);
    setEditModalOpen(true);
  };
  // Handler zum Speichern der Änderungen
  const handleSaveEditEntry = () => {
    setEntries(prev => prev.map(e => e.id === editEntry.id ? editEntry : e));
    setEditModalOpen(false);
    setEditEntry(null);
  };

  // Filtern und Sortieren von Einträgen
  const getFilteredAndSortedEntries = () => {
    let filtered = [...entries];
    
    // Status-Filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(item => {
        const hasBeginn = typeof item.arbeitsbeginn === 'string' && item.arbeitsbeginn.trim() !== '';
        const hasEnde = typeof item.arbeitsende === 'string' && item.arbeitsende.trim() !== '';
        if (selectedStatus === 'offen') {
          return !hasBeginn && !hasEnde;
        }
        if (selectedStatus === 'in Bearbeitung') {
          return hasBeginn && !hasEnde;
        }
        if (selectedStatus === 'abgeschlossen') {
          return hasBeginn && hasEnde;
        }
        return true;
      });
    }
    
    // Anwenden der Suchfilter
    if (searchTerm) {
      const searchTermLower = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.tanks.toLowerCase().includes(searchTermLower) ||
        item.firma.toLowerCase().includes(searchTermLower) ||
        item.nummer.toString().includes(searchTermLower) ||
        item.datum.toLowerCase().includes(searchTermLower) ||
        (item.freigemessen ? "freigemessen" : "nicht freigemessen").includes(searchTermLower)
        );
    }
    
    // Sortieren nach ID (Nummer) je nach State
    filtered = filtered.sort((a, b) => {
      const numA = Number(a.nummer) || 0;
      const numB = Number(b.nummer) || 0;
      return sortByIdDesc ? numB - numA : numA - numB;
    });
    return filtered;
  };

  // Gefilterte und sortierte Einträge für die Anzeige
  const displayEntries = getFilteredAndSortedEntries();

  // Neue Handler-Funktion für Bemerkungen
  const handleBemerkungChange = (id, value) => {
    setEntries(prev => prev.map(entry => entry.id === id ? { ...entry, bemerkung: value } : entry));
  };

  // E-Mail-Einstellungen Funktionen
  const handleOpenEmailSettings = async () => {
    setShowEmailSettings(true);
    // Lade gespeicherte E-Mail-Einstellungen aus Firebase
    try {
      const { getDoc, doc } = await import('firebase/firestore');
      const { db } = await import('../firebaseConfig');
      
      const settingsDoc = await getDoc(doc(db, 'settings', 'emailSettings'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setEmailTo(data.emailTo || '');
        setEmailCC(data.emailCC || '');
      }
    } catch (error) {
      console.error('Fehler beim Laden der E-Mail-Einstellungen:', error);
      setMessage({ text: "Fehler beim Laden der Einstellungen", type: "error" });
    }
  };

  const handleSaveEmailSettings = async () => {
    try {
      const { setDoc, doc } = await import('firebase/firestore');
      const { db } = await import('../firebaseConfig');
      
      await setDoc(doc(db, 'settings', 'emailSettings'), {
        emailTo: emailTo,
        emailCC: emailCC,
        lastUpdated: new Date().toISOString()
      });
      
      setShowEmailSettings(false);
      setMessage({ text: "E-Mail-Einstellungen in Firebase gespeichert!", type: "success" });
    } catch (error) {
      console.error('Fehler beim Speichern der E-Mail-Einstellungen:', error);
      setMessage({ text: "Fehler beim Speichern der Einstellungen", type: "error" });
    }
  };

  const handleCloseEmailSettings = () => {
    setShowEmailSettings(false);
  };

  // Time-Picker Funktionen
  const handleTimePickerOpen = (entryId, field) => {
    setActiveTimePicker({ entryId, field });
    
    // Setze aktuelle Echtzeit, wenn das Feld leer ist
    const entry = entries.find(e => e.id === entryId);
    if (entry) {
      const currentTime = entry[field];
      if (!currentTime || currentTime === '') {
        const now = new Date();
        const timeString = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
        handleArbeitszeitChange(entryId, field, timeString);
        console.log('Set current real time to field:', timeString);
      }
    }
  };

  const handleTimePickerClose = () => {
    setActiveTimePicker(null);
  };

  // Event-Listener für Klick außerhalb des Time-Pickers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeTimePicker && !event.target.closest('.time-picker-dropdown')) {
        handleTimePickerClose();
      }
    };

    if (activeTimePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeTimePicker]);

  const handleTimeChange = (entryId, field, hours, minutes) => {
    const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    handleArbeitszeitChange(entryId, field, timeString);
  };

  const getCurrentTime = (timeString) => {
    console.log('getCurrentTime called with:', timeString);
    
    // Verwende aktuelle Echtzeit
    const now = new Date();
    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();
    
    console.log('Current real time:', currentHours + ':' + currentMinutes);
    
    // Wenn eine Zeit im Eingabefeld steht, verwende diese, sonst aktuelle Echtzeit
    if (timeString && timeString !== '') {
      const [hours, minutes] = timeString.split(':').map(Number);
      const result = { hours: hours || currentHours, minutes: minutes || currentMinutes };
      console.log('Using input time:', result);
      return result;
    } else {
      const result = { hours: currentHours, minutes: currentMinutes };
      console.log('Using real time:', result);
      return result;
    }
  };

  // Hilfsfunktion zur Admin-Erkennung
  const isAdmin = currentUser && (currentUser.role === "admin" || currentUser.role === "superadmin");

  // TimePickerDropdown Komponente
  const TimePickerDropdown = ({ currentTime, onTimeChange, onClose }) => {
    const [selectedHours, setSelectedHours] = useState(currentTime.hours);
    const [selectedMinutes, setSelectedMinutes] = useState(currentTime.minutes);

    // Aktualisiere die ausgewählte Zeit, wenn sich currentTime ändert
    useEffect(() => {
      setSelectedHours(currentTime.hours);
      setSelectedMinutes(currentTime.minutes);
    }, [currentTime]);

    // Scroll immer nach oben, da aktuelle Zeit ganz oben steht
    useEffect(() => {
      setTimeout(() => {
        const hoursElement = document.querySelector(`[data-time-picker="${currentTime.hours}-${currentTime.minutes}"] .hours-scroll`);
        const minutesElement = document.querySelector(`[data-time-picker="${currentTime.hours}-${currentTime.minutes}"] .minutes-scroll`);
        
        if (hoursElement) {
          hoursElement.scrollTop = 0; // Immer ganz oben
        }
        if (minutesElement) {
          minutesElement.scrollTop = 0; // Immer ganz oben
        }
      }, 100); // Kurze Verzögerung für DOM-Rendering
    }, [currentTime]);

    // Erstelle Listen mit aktueller Zeit ganz oben
    const hours = [
      selectedHours, // Aktuelle Stunde ganz oben
      ...Array.from({ length: 24 }, (_, i) => i).filter(h => h !== selectedHours)
    ];
    const minutes = [
      selectedMinutes, // Aktuelle Minute ganz oben
      ...Array.from({ length: 60 }, (_, i) => i).filter(m => m !== selectedMinutes)
    ];

    const handleHoursChange = (hours) => {
      setSelectedHours(hours);
      onTimeChange(hours, selectedMinutes);
    };

    const handleMinutesChange = (minutes) => {
      setSelectedMinutes(minutes);
      onTimeChange(selectedHours, minutes);
    };

    return (
      <div 
        className="time-picker-dropdown"
        data-time-picker={`${currentTime.hours}-${currentTime.minutes}`}
        style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          zIndex: 1000,
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '10px',
          display: 'flex',
          gap: '10px',
          width: '152px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <label style={{ fontSize: '12px', marginBottom: '5px', fontWeight: 'bold' }}>Stunden</label>
          <div 
            className="hours-scroll"
            style={{ 
              height: '120px', 
              overflowY: 'auto', 
              border: '1px solid #ddd', 
              borderRadius: '4px',
              width: '60px'
            }}
          >
            {hours.map(hour => (
              <div
                key={hour}
                onClick={() => handleHoursChange(hour)}
                style={{
                  padding: '5px 10px',
                  cursor: 'pointer',
                  backgroundColor: selectedHours === hour ? '#007bff' : 'transparent',
                  color: selectedHours === hour ? '#fff' : '#000',
                  textAlign: 'center',
                  fontSize: '14px'
                }}
              >
                {hour.toString().padStart(2, '0')}
              </div>
            ))}
          </div>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <label style={{ fontSize: '12px', marginBottom: '5px', fontWeight: 'bold' }}>Minuten</label>
          <div 
            className="minutes-scroll"
            style={{ 
              height: '120px', 
              overflowY: 'auto', 
              border: '1px solid #ddd', 
              borderRadius: '4px',
              width: '60px'
            }}
          >
            {minutes.map(minute => (
              <div
                key={minute}
                onClick={() => handleMinutesChange(minute)}
                style={{
                  padding: '5px 10px',
                  cursor: 'pointer',
                  backgroundColor: selectedMinutes === minute ? '#007bff' : 'transparent',
                  color: selectedMinutes === minute ? '#fff' : '#000',
                  textAlign: 'center',
                  fontSize: '14px'
                }}
              >
                {minute.toString().padStart(2, '0')}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="enge-raeume-container">
      <div className="sticky-header">
        <div className="form-container">
          {/* Linke Spalte */}
          <div className="form-column">
            <div className="form-group">
              <label>Abgabe Schein</label>
              <input
                type="date"
                value={abgabeDatum}
                onChange={(e) => setAbgabeDatum(e.target.value)}
              />
            </div>
            {/* Button-Container - nur in der linken Spalte */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* Erste Reihe Buttons - gleich lang */}
              <div style={{ display: 'flex', gap: '30px' }}>
                <button
                  onClick={handleAddEntry}
                  className="eintragen-btn"
                  disabled={isLoading}
                  style={{ width: '350px' }}
                >
                  Eintragen
                </button>
                <button
                  onClick={handleSaveData}
                  className="speichern-btn"
                  disabled={isLoading}
                  style={{ width: '350px' }}
                >
                  Speichern
                </button>
              </div>
              
              {/* Zweite Reihe Buttons - gleich lang */}
              <div style={{ display: 'flex', gap: '30px' }}>
                <button 
                  onClick={findTanksForTomorrow}
                  className="tank-order-button"
                  style={{ width: '350px' }}
                >
                  Tanks bestellen
                </button>
                <button 
                  onClick={handleOpenEmailSettings}
                  className="einstellungen-btn"
                  style={{ 
                    background: '#64b5f6', 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: '4px', 
                    padding: '8px 12px', 
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    width: '350px'
                  }}
                >
                  Einstellungen
                </button>
              </div>
            </div>
          </div>
          
          {/* Mittlere Spalte */}
          <div className="form-column">
            <div className="form-group">
              <label>Zeitraum der Freigabe (von)</label>
                <input
                  type="date"
                  value={freigabeVon}
                  onChange={(e) => setFreigabeVon(e.target.value)}
                />
            </div>
            <div className="form-group">
              <label>Zeitraum der Freigabe (bis)</label>
                <input
                  type="date"
                  value={freigabeBis}
                  onChange={(e) => setFreigabeBis(e.target.value)}
                />
              </div>
            <button
              type="button"
              className="secondary-action-btn"
              style={{ minWidth: '50px' }}
              disabled={isLoading}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              Datei Import
              </button>
              <input 
                type="file" 
                accept=".csv,.xlsx,.xls" 
                onChange={handleFileUpload} 
                disabled={isLoading}
              ref={fileInputRef}
                style={{ display: 'none' }}
              />
          </div>
          
          {/* Rechte Spalte */}
          <div className="form-column">
            <div className="form-group">
              <label>Tanks</label>
              <input
                type="text"
                value={tanks}
                onChange={(e) => setTanks(e.target.value)}
                placeholder="z.B. T123, T456"
              />
            </div>
            
            <div className="form-group">
              <label>Firma</label>
              <input
                type="text"
                value={firma}
                onChange={(e) => setFirma(e.target.value)}
                placeholder="Firmenname eingeben"
              />
            </div>
            
            <div className="enge-raeume-filters">
              <div className="search-container">
              <input
                type="text"
                placeholder="Suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
              <div className="filter-container" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">Alle Status</option>
                  <option value="offen">Offen</option>
                  <option value="in Bearbeitung">In Bearbeitung</option>
                  <option value="abgeschlossen">Abgeschlossen</option>
                </select>
                <button
                  type="button"
                  style={{ height: 32, padding: '0 14px', borderRadius: 4, background: showTrash ? '#d32f2f' : '#e0e0e0', color: showTrash ? '#fff' : '#333', border: 'none', fontWeight: 600, cursor: 'pointer' }}
                  onClick={() => setShowTrash((v) => !v)}
                  title="Papierkorb anzeigen"
                >
                  {showTrash ? 'Zurück' : 'Papierkorb'}
                </button>
            </div>
          </div>
        </div>
      </div>
      <div className="divider"></div>
    </div>
    <div className="entries-section">
      {isLoading ? (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Daten werden geladen...</p>
        </div>
      ) : (
        showTrash ? (
          <div className="entries-section">
        <table className="entries-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Datum</th>
              <th>Tanks</th>
              <th>Firma</th>
              <th>Beginn</th>
              <th>Ende</th>
              <th>OK</th>
                  <th>Bemerkungen</th>
                  <th>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {trashEntries.length > 0 ? (
                  trashEntries.map((entry) => (
                    <tr key={entry.id}>
                      <td>{entry.nummer}</td>
                      <td>{entry.datum}</td>
                      <td>{entry.tanks}</td>
                      <td>{entry.firma}</td>
                      <td>{entry.arbeitsbeginn}</td>
                      <td>{entry.arbeitsende}</td>
                      <td>{entry.freigemessen ? "Ja" : "Nein"}</td>
                      <td>{entry.bemerkung || ""}</td>
                      <td style={{display: 'flex', gap: 6}}>
                        <button
                          className="restore-btn"
                          style={{background: '#4caf50', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer'}} 
                          onClick={() => handleRestoreFromTrash(entry.id)}
                          title="Wiederherstellen"
                        >
                          Wiederherstellen
                        </button>
                        {isAdmin && (
                          <button
                            className="delete-btn"
                            style={{background: '#d32f2f', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer'}} 
                            onClick={() => handleDeleteForever(entry.id)}
                            title="Endgültig löschen"
                          >
                            Endgültig löschen
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="no-entries">
                      Papierkorb ist leer
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <table className="entries-table">
            <thead>
              <tr>
                <th style={{cursor: 'pointer'}} onClick={() => setSortByIdDesc(s => !s)}>
                  ID {sortByIdDesc ? '▼' : '▲'}
                </th>
                <th>Datum</th>
                <th>Tanks</th>
                <th>Firma</th>
                <th>Beginn</th>
                <th>Ende</th>
                <th>OK</th>
                <th>Bemerkungen</th>
              <th>🗑</th>
            </tr>
          </thead>
          <tbody>
            {displayEntries.length > 0 ? (
              displayEntries.map((entry) => (
                <tr key={entry.id} className={entry.freigemessen ? "freigemessen" : ""}>
                  <td onClick={() => handleEditEntry(entry)} style={{cursor:'pointer'}}>{entry.nummer}</td>
                  <td>{entry.datum}</td>
                  <td>{entry.tanks}</td>
                  <td>{entry.firma}</td>
                  <td style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={entry.arbeitsbeginn}
                      onChange={(e) =>
                        handleArbeitszeitChange(entry.id, "arbeitsbeginn", e.target.value)
                      }
                      readOnly
                      disabled={!entry.freigemessen}
                      onClick={() => entry.freigemessen && handleTimePickerOpen(entry.id, 'arbeitsbeginn')}
                      style={{
                        backgroundColor: entry.freigemessen ? '#fff' : '#f5f5f5',
                        color: entry.freigemessen ? '#000' : '#999',
                        cursor: entry.freigemessen ? 'pointer' : 'not-allowed'
                      }}
                    />
                    {activeTimePicker && activeTimePicker.entryId === entry.id && activeTimePicker.field === 'arbeitsbeginn' && (
                      <TimePickerDropdown
                        currentTime={getCurrentTime(entry.arbeitsbeginn)}
                        onTimeChange={(hours, minutes) => handleTimeChange(entry.id, 'arbeitsbeginn', hours, minutes)}
                        onClose={handleTimePickerClose}
                      />
                    )}
                  </td>
                  <td style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={entry.arbeitsende}
                      onChange={(e) =>
                        handleArbeitszeitChange(entry.id, "arbeitsende", e.target.value)
                      }
                      readOnly
                      disabled={!entry.freigemessen}
                      onClick={() => entry.freigemessen && handleTimePickerOpen(entry.id, 'arbeitsende')}
                      style={{
                        backgroundColor: entry.freigemessen ? '#fff' : '#f5f5f5',
                        color: entry.freigemessen ? '#000' : '#999',
                        cursor: entry.freigemessen ? 'pointer' : 'not-allowed'
                      }}
                    />
                    {activeTimePicker && activeTimePicker.entryId === entry.id && activeTimePicker.field === 'arbeitsende' && (
                      <TimePickerDropdown
                        currentTime={getCurrentTime(entry.arbeitsende)}
                        onTimeChange={(hours, minutes) => handleTimeChange(entry.id, 'arbeitsende', hours, minutes)}
                        onClose={handleTimePickerClose}
                      />
                    )}
                  </td>
                    <td className="ok-column">
                    <input
                      type="checkbox"
                      checked={entry.freigemessen}
                        onChange={(e) => handleFreigemessenChange(entry.id, e.target.checked)}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={entry.bemerkung || ""}
                        onChange={e => handleBemerkungChange(entry.id, e.target.value)}
                        placeholder="Bemerkung..."
                        style={{width: '100%'}}
                    />
                  </td>
                  <td>
                    <button 
                      className="delete-btn" 
                        onClick={() => handleDeleteById(entry.id)}
                      title="Löschen"
                    >
                      🗑
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                  <td colSpan="9" className="no-entries">
                  Keine Einträge gefunden
                </td>
              </tr>
            )}
          </tbody>
        </table>
        )
      )}
    </div>

    {/* Tankbestellung Modal */}
    {showTankModal && (
      <TankbestellungModal
        entries={tanksForTomorrow}
        formattedDate={tomorrowFormattedDate}
        onClose={() => setShowTankModal(false)}
      />
    )}

    {/* Bearbeiten Modal */}
    {editModalOpen && (
      <div className="modal-overlay">
        <div className="modal-content" style={{ width: '700px' }}>
          <h2>Eintrag bearbeiten</h2>
          <div className="form-group">
            <label>Abgabe Schein</label>
            <input type="date" value={editEntry.abgabeDatum} onChange={e => setEditEntry({...editEntry, abgabeDatum: e.target.value})} style={{ width: '350px' }} />
          </div>
          <div className="form-group">
            <label>Datum</label>
            <input type="date" value={editEntry.datum} onChange={e => setEditEntry({...editEntry, datum: e.target.value})} style={{ width: '350px' }} />
          </div>
          <div className="form-group">
            <label>Tanks</label>
            <input type="text" value={editEntry.tanks} onChange={e => setEditEntry({...editEntry, tanks: e.target.value})} style={{ width: '350px' }} />
          </div>
          <div className="form-group">
            <label>Firma</label>
            <input type="text" value={editEntry.firma} onChange={e => setEditEntry({...editEntry, firma: e.target.value})} style={{ width: '350px' }} />
          </div>
          <div className="form-group">
            <label>Arbeitsbeginn</label>
            <input type="time" value={editEntry.arbeitsbeginn} onChange={e => setEditEntry({...editEntry, arbeitsbeginn: e.target.value})} style={{ width: '350px' }} />
          </div>
          <div className="form-group">
            <label>Arbeitsende</label>
            <input type="time" value={editEntry.arbeitsende} onChange={e => setEditEntry({...editEntry, arbeitsende: e.target.value})} style={{ width: '350px' }} />
          </div>
          <div className="form-group">
            <label>Freigemessen</label>
            <input type="checkbox" checked={editEntry.freigemessen} onChange={e => setEditEntry({...editEntry, freigemessen: e.target.checked})} />
          </div>
          <div className="form-group">
            <label>Bemerkung</label>
            <input type="text" value={editEntry.bemerkung || ""} onChange={e => setEditEntry({...editEntry, bemerkung: e.target.value})} style={{ width: '350px' }} />
          </div>
          <div style={{display:'flex',gap:8,marginTop:16}}>
            <button onClick={() => {setEditModalOpen(false);setEditEntry(null);}}>Abbrechen</button>
            <button onClick={handleSaveEditEntry} style={{background:'#4caf50',color:'#fff'}}>Speichern</button>
          </div>
        </div>
      </div>
    )}

    {/* E-Mail-Einstellungen Modal */}
    {showEmailSettings && (
      <div className="modal-overlay">
        <div className="modal-content" style={{ width: '1000px !important', minWidth: '1000px', maxWidth: '1000px' }}>
          <h3>E-Mail-Einstellungen</h3>
          <div className="form-group">
            <label>An:</label>
            <input 
              type="email" 
              value={emailTo} 
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="E-Mail-Adresse eingeben"
              style={{ width: '900px', height: '40px', fontSize: '16px', padding: '8px 12px', boxSizing: 'border-box' }}
            />
          </div>
          <div className="form-group">
            <label>CC:</label>
            <input 
              type="email" 
              value={emailCC} 
              onChange={(e) => setEmailCC(e.target.value)}
              placeholder="CC E-Mail-Adresse eingeben"
              style={{ width: '900px', height: '40px', fontSize: '16px', padding: '8px 12px', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <button onClick={handleCloseEmailSettings}>Abbrechen</button>
            <button onClick={handleSaveEmailSettings} style={{ background: '#4caf50', color: '#fff' }}>
              Speichern
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
);
}

export default EngeRaeume;